"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import MediaSplitView from "@/components/optilase/home/media-split-view";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { richParagraphs } from "@/lib/richtext";
import { surfaceFrom } from "@/components/optilase/surface";
import { buttonVariant } from "@/lib/button-color";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string; focus?: string };

type TextWithImageBlok = SbBlokData & {
  title?: string;
  description?: unknown;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: string;
  button_style?: string;
  button_color?: string;
  image?: Asset;
  badge_text?: string;
  badge_image?: { filename?: string; alt?: string };
  side_content?: string;
  list_items?: string;
  list_in_card?: boolean;
  card_scope?: string;
  list_marker?: string;
  list_heading?: string;
  list_columns?: string;
  list_position?: string;
  footnote?: string;
  footnote_position?: string;
  heading_size?: string;
  body_size?: string;
  heading_color?: string;
  spacing?: string;
  list_tick_color?: string;
  image_size?: string;
  reverse_order?: boolean;
  split_from_tablet?: boolean;
  source_ref?: string;
  bg_color?: string;
};

const HEADING_COLOURS: Record<string, string | undefined> = {
  teal_07: "text-teal-07",
  teal_08: "text-teal-08",
  white: "text-white",
  navy: "text-navy-03",
};

export default function GlobalTextWithImageList({ blok }: { blok: TextWithImageBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const link = resolveLink(blok.button_link, region);

  const listItems =
    blok.side_content === "list" || blok.list_position === "below"
      ? (blok.list_items ?? "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean)
      : [];

  const imageContain = blok.image_size === "contain";

  const imageHeightClass =
    blok.image_size === "small"
      ? "h-80 md:h-[225px] lg:h-[329px]"
      : "h-65 md:h-[336px] lg:h-[400px]";

  return (
    <div {...storyblokEditable(blok)}>
      <MediaSplitView
        surface={surfaceFrom(blok.bg_color)}
        heading={blok.title ?? ""}
        bodyNodes={richParagraphs(blok.description, region, surfaceFrom(blok.bg_color)?.onDark)}
        image={blok.image?.filename ?? ""}
        imageAlt={blok.image?.alt ?? ""}
        imageFocus={blok.image?.focus}
        imageBadge={blok.badge_text || undefined}
        imageBadgeSrc={blok.badge_image?.filename || undefined}
        imageBadgeAlt={blok.badge_image?.alt || undefined}
        ctaLabel={blok.button_label || undefined}
        ctaHref={link.href}
        ctaAsLink={blok.button_style !== "solid"}
        ctaVariant={buttonVariant(blok.button_color)}
        ctaAction={blok.button_action}
        splitFrom={blok.split_from_tablet ? "md" : "lg"}
        listItems={listItems}
        listInCard={blok.list_in_card ?? false}
        cardScope={blok.card_scope === "row" ? "row" : "list"}
        listMarker={blok.list_marker === "dot" ? "dot" : "tick"}
        listTick={blok.list_tick_color === "navy" ? "navy" : "teal"}
        reversed={blok.reverse_order ?? false}
        listHeading={blok.list_heading}
        listColumns={blok.list_columns === "two" ? 2 : 1}
        listPosition={blok.list_position === "below" ? "below" : "side"}
        footnote={blok.footnote}
        footnotePosition={blok.footnote_position === "outside" ? "outside" : "inside"}
        headingSize={blok.heading_size === "sub" ? "sub" : "section"}
        spacing={blok.spacing}
        headingColor={HEADING_COLOURS[blok.heading_color ?? ""]}
        bodySize={
          blok.body_size === "small" || blok.body_size === "large" ? blok.body_size : "medium"
        }
        imageHeightClass={imageHeightClass}
        imageFill={blok.image_size === "fill"}
        imageContain={imageContain}
      />
    </div>
  );
}
